import React, { useEffect, useMemo, useState } from 'react';
import { UnifiedTransaction, TFN } from '../types/payment';
import { TransactionAPI, SettingsAPI } from '../services/api';
import { AlertService } from '../services/AlertService';
import { ALLOWED_CATEGORIES, US_STATES, US_STATE_CODES } from '../constants/enums';

const SALE_DOMAINS = ['Electricity', 'Gas', 'Water'];

interface Props {
  transaction: UnifiedTransaction;
  onClose: () => void;
  onSaved?: () => void;
}

// `opts` names a dropdown source (resolved in the component so TFN can be async).
// 'date' fields auto-format to MM/DD/YYYY as you type — same as the payment form.
type Field = {
  key: string;
  label: string;
  type?: 'text' | 'number' | 'checkbox' | 'select' | 'date';
  opts?: 'US_STATES' | 'US_STATE_CODES' | 'ALLOWED_CATEGORIES' | 'TFN';
};
type Section = { title: string; fields: Field[] };

// Auto-insert slashes → MM/DD/YYYY (matches the New Payment form's date format).
const formatMDY = (value: string): string => {
  const d = (value || '').replace(/\D/g, '').slice(0, 8);
  if (d.length <= 2) return d;
  if (d.length <= 4) return `${d.slice(0, 2)}/${d.slice(2)}`;
  return `${d.slice(0, 2)}/${d.slice(2, 4)}/${d.slice(4)}`;
};

// Card data is intentionally absent — it is never editable.
// Section order + field grouping mirror the New Payment form's layout:
// Sale Information → Payment → Order Information → Billing → Shipping.
// The domains/providers editor renders inside Sale Information and the
// sensitive (SSN/DL#) fields inside Order Information (see the render).
const SECTIONS: Section[] = [
  {
    title: 'Sale Information',
    fields: [
      { key: 'category', label: 'Sale Type', type: 'select', opts: 'ALLOWED_CATEGORIES' },
      { key: 'sale_state', label: 'Sale State', type: 'select', opts: 'US_STATES' },
      { key: 'sale_notes', label: 'Sale Notes' },
    ],
  },
  {
    title: 'Payment',
    fields: [
      { key: 'tfn', label: 'TFN', type: 'select', opts: 'TFN' },
    ],
  },
  {
    title: 'Order Information',
    fields: [
      { key: 'orderid', label: 'Order ID' },
      { key: 'ponumber', label: 'PO Number' },
      { key: 'order_description', label: 'Order Description' },
      { key: 'shipping_amount', label: 'Shipping Amount', type: 'number' },
      { key: 'sales_tax', label: 'Sales Tax', type: 'number' },
      { key: 'tax_exempt', label: 'Tax Exempt', type: 'checkbox' },
      { key: 'date_of_birth', label: 'Date of Birth (MM/DD/YYYY)', type: 'date' },
      { key: 'account_number', label: 'Account Number' },
      { key: 'drivers_license_state', label: 'Issuing State', type: 'select', opts: 'US_STATE_CODES' },
      { key: 'dl_issuing_date', label: 'DLN Issuing Date', type: 'date' },
      { key: 'dl_expiration_date', label: 'DLN Expiration Date', type: 'date' },
    ],
  },
  {
    title: 'Billing Information',
    fields: [
      { key: 'first_name', label: 'First Name' },
      { key: 'middle_name', label: 'Middle Name' },
      { key: 'last_name', label: 'Last Name' },
      { key: 'company', label: 'Company' },
      { key: 'address1', label: 'Address' },
      { key: 'address2', label: 'Address Line 2' },
      { key: 'city', label: 'City' },
      { key: 'state', label: 'State', type: 'select', opts: 'US_STATES' },
      { key: 'zip', label: 'ZIP Code' },
      { key: 'country', label: 'Country' },
      { key: 'phone', label: 'Phone Number' },
      { key: 'email', label: 'Email Address' },
    ],
  },
  {
    title: 'Service Address',
    fields: [
      { key: 'shipping_firstname', label: 'First Name' },
      { key: 'shipping_lastname', label: 'Last Name' },
      { key: 'shipping_company', label: 'Company' },
      { key: 'shipping_address_1', label: 'Address Line 1' },
      { key: 'shipping_address_2', label: 'Address Line 2' },
      { key: 'shipping_city', label: 'City' },
      { key: 'shipping_state', label: 'State', type: 'select', opts: 'US_STATES' },
      { key: 'shipping_zip', label: 'ZIP Code' },
      { key: 'shipping_country', label: 'Country' },
    ],
  },
];

// Sensitive fields: only sent if the user types a new value. The current value
// is never shown (only its last-4, as a placeholder hint).
const SENSITIVE: { key: string; label: string; last4Key: string; numeric?: boolean; maxLength?: number }[] = [
  { key: 'ssn', label: 'SSN', last4Key: 'last4_ssn', numeric: true, maxLength: 9 },
  { key: 'drivers_license_number', label: "Driver's License #", last4Key: 'dl_last4' },
];

export const SaleEditModal: React.FC<Props> = ({ transaction, onClose, onSaved }) => {
  const t = transaction as any;
  const recordId = t.transaction_id || t.id;

  // Initialise form from the current record.
  const initial = useMemo(() => {
    const obj: Record<string, any> = {};
    SECTIONS.forEach(s => s.fields.forEach(f => {
      const v = t[f.key];
      obj[f.key] = f.type === 'checkbox' ? !!v : (v ?? '');
    }));
    return obj;
  }, [t]);

  const [form, setForm] = useState<Record<string, any>>(initial);
  const [sensitive, setSensitive] = useState<Record<string, string>>({ ssn: '', drivers_license_number: '' });
  const [saving, setSaving] = useState(false);

  // Active TFNs for the TFN dropdown (same source as the New Payment form).
  const [tfns, setTfns] = useState<TFN[]>([]);
  useEffect(() => {
    SettingsAPI.getTFNs(true).then((res: any) => setTfns(res?.tfns || [])).catch(() => setTfns([]));
  }, []);

  // Resolve a dropdown source name → {value,label}[].
  const optionsFor = (opts?: Field['opts']): { value: string; label: string }[] => {
    switch (opts) {
      case 'US_STATES': return US_STATES.map(s => ({ value: s, label: s }));
      case 'US_STATE_CODES': return US_STATE_CODES.map(s => ({ value: s, label: s }));
      case 'ALLOWED_CATEGORIES': return ALLOWED_CATEGORIES.map(s => ({ value: s, label: s }));
      case 'TFN': return tfns.map(t2 => ({ value: t2.number, label: t2.label ? `${t2.label} (${t2.number})` : t2.number }));
      default: return [];
    }
  };

  // Structured fields: sale_domains (subset of SALE_DOMAINS) + utility_providers
  // ({ domain: providerId }). Providers per domain are loaded on demand.
  const origDomains = useMemo<string[]>(() => Array.isArray(t.sale_domains) ? t.sale_domains : [], [t]);
  const origProviders = useMemo<Record<string, string>>(
    () => (t.utility_providers && typeof t.utility_providers === 'object') ? t.utility_providers : {},
    [t]
  );
  const [domains, setDomains] = useState<string[]>(() => [...origDomains]);
  const [providersSel, setProvidersSel] = useState<Record<string, string>>(() => ({ ...origProviders }));
  const [providersByDomain, setProvidersByDomain] = useState<Record<string, any[]>>({});

  const loadProviders = async (domain: string) => {
    setProvidersByDomain(prev => prev[domain] ? prev : { ...prev, [domain]: [] });
    try {
      const list = await SettingsAPI.getProvidersByDomain(domain);
      setProvidersByDomain(prev => ({ ...prev, [domain]: list || [] }));
    } catch {
      setProvidersByDomain(prev => ({ ...prev, [domain]: [] }));
    }
  };

  // Load provider options for domains already on the sale.
  useEffect(() => { origDomains.forEach(loadProviders); /* eslint-disable-next-line */ }, [origDomains]);

  const toggleDomain = (domain: string, checked: boolean) => {
    if (checked) {
      setDomains(prev => prev.includes(domain) ? prev : [...prev, domain]);
      loadProviders(domain);
    } else {
      setDomains(prev => prev.filter(d => d !== domain));
      setProvidersSel(prev => { const n = { ...prev }; delete n[domain]; return n; });
    }
  };
  const setProvider = (domain: string, id: string) =>
    setProvidersSel(prev => { const n = { ...prev }; if (id) n[domain] = id; else delete n[domain]; return n; });

  const setField = (k: string, v: any) => setForm(prev => ({ ...prev, [k]: v }));

  const handleSave = async () => {
    // Validate DLN dates: issue date can't be in the future, expiry can't be past.
    const parseMDY = (v?: string): Date | null => {
      const m = (v || '').match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
      if (!m) return null;
      const d = new Date(Number(m[3]), Number(m[1]) - 1, Number(m[2]));
      return isNaN(d.getTime()) ? null : d;
    };
    const today0 = new Date(); today0.setHours(0, 0, 0, 0);
    const dob = parseMDY(form.date_of_birth);
    if (dob && dob > today0) { AlertService.error('Date of birth cannot be in the future'); return; }
    const iss = parseMDY(form.dl_issuing_date);
    if (iss && iss > today0) { AlertService.error('DLN issuing date cannot be in the future'); return; }
    const exp = parseMDY(form.dl_expiration_date);
    if (exp && exp < today0) { AlertService.error('DLN expiration date cannot be in the past'); return; }

    // Only send fields that actually changed.
    const payload: Record<string, any> = {};
    SECTIONS.forEach(s => s.fields.forEach(f => {
      const cur = form[f.key];
      const orig = initial[f.key];
      if (f.type === 'number') {
        const n = cur === '' || cur === null ? null : Number(cur);
        if (n !== (orig === '' ? null : Number(orig))) payload[f.key] = n;
      } else if (f.type === 'checkbox') {
        if (!!cur !== !!orig) payload[f.key] = !!cur;
      } else if ((cur ?? '') !== (orig ?? '')) {
        payload[f.key] = cur === '' ? null : cur;
      }
    }));
    // Structured fields: include only if changed.
    if (JSON.stringify(domains) !== JSON.stringify(origDomains)) payload.sale_domains = domains;
    if (JSON.stringify(providersSel) !== JSON.stringify(origProviders)) payload.utility_providers = providersSel;

    // Sensitive: include only if a new value was typed.
    SENSITIVE.forEach(s => {
      if (sensitive[s.key] && sensitive[s.key].trim()) payload[s.key] = sensitive[s.key].trim();
    });

    if (Object.keys(payload).length === 0) {
      AlertService.error('No changes to save');
      return;
    }
    try {
      setSaving(true);
      await TransactionAPI.updateSaleDetails(recordId, payload);
      AlertService.success('Sale details updated');
      onSaved?.();
      onClose();
    } catch (e: any) {
      AlertService.error(e.message || 'Failed to save changes');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="sale-edit-overlay" onClick={onClose}>
      <div className="sale-edit-modal" onClick={e => e.stopPropagation()}>
        <div className="sale-edit-header">
          <h2>Edit Sale Details</h2>
          <small>Card information cannot be edited. Amount is fixed to the processed charge.</small>
        </div>

        <div className="sale-edit-body">
          {SECTIONS.map(section => (
            <div key={section.title} className="sale-edit-section">
              <h3>{section.title}</h3>
              <div className="sale-edit-grid">
                {section.fields.map(f => (
                  <div key={f.key} className={`sale-edit-field ${f.type === 'checkbox' ? 'is-check' : ''}`}>
                    <label>{f.label}</label>
                    {f.type === 'checkbox' ? (
                      <input
                        type="checkbox"
                        checked={!!form[f.key]}
                        onChange={e => setField(f.key, e.target.checked)}
                      />
                    ) : f.type === 'select' ? (
                      <select value={form[f.key] ?? ''} onChange={e => setField(f.key, e.target.value)}>
                        <option value="">— Select —</option>
                        {/* Keep the current value selectable even if it's not in the list. */}
                        {form[f.key] && !optionsFor(f.opts).some(o => o.value === form[f.key]) && (
                          <option value={form[f.key]}>{form[f.key]}</option>
                        )}
                        {optionsFor(f.opts).map(o => (
                          <option key={o.value} value={o.value}>{o.label}</option>
                        ))}
                      </select>
                    ) : f.type === 'date' ? (
                      <input
                        type="text"
                        inputMode="numeric"
                        placeholder="MM/DD/YYYY"
                        value={form[f.key] ?? ''}
                        onChange={e => setField(f.key, formatMDY(e.target.value))}
                      />
                    ) : (
                      <input
                        type={f.type === 'number' ? 'number' : 'text'}
                        value={form[f.key] ?? ''}
                        onChange={e => setField(f.key, e.target.value)}
                      />
                    )}
                  </div>
                ))}
              </div>

              {section.title === 'Sale Information' && (
                <div style={{ marginTop: '0.9rem' }}>
                  <label style={{ fontSize: '0.8rem', color: 'var(--text-secondary, #6b7280)', display: 'block', marginBottom: '0.4rem' }}>Sale Domains &amp; Providers</label>
                  <div style={{ display: 'flex', gap: '1.25rem', marginBottom: '0.75rem', flexWrap: 'wrap' }}>
                    {SALE_DOMAINS.map(d => (
                      <label key={d} style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                        <input type="checkbox" checked={domains.includes(d)} onChange={e => toggleDomain(d, e.target.checked)} />
                        {d}
                      </label>
                    ))}
                  </div>
                  <div className="sale-edit-grid">
                    {domains.map(d => (
                      <div key={d} className="sale-edit-field">
                        <label>{d} Provider</label>
                        <select value={providersSel[d] || ''} onChange={e => setProvider(d, e.target.value)}>
                          <option value="">Select a provider…</option>
                          {(providersByDomain[d] || []).map((p: any) => (
                            <option key={p.id} value={p.id}>{p.name}</option>
                          ))}
                        </select>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {section.title === 'Order Information' && (
                <div style={{ marginTop: '0.9rem' }}>
                  <label style={{ fontSize: '0.8rem', color: 'var(--text-secondary, #6b7280)', display: 'block', marginBottom: '0.4rem' }}>Sensitive (leave blank to keep current)</label>
                  <div className="sale-edit-grid">
                    {SENSITIVE.map(s => (
                      <div key={s.key} className="sale-edit-field">
                        <label>{s.label}</label>
                        <input
                          type="text"
                          inputMode={s.numeric ? 'numeric' : undefined}
                          maxLength={s.maxLength}
                          value={sensitive[s.key]}
                          placeholder={t[s.last4Key] ? `•••• ${t[s.last4Key]} — enter new to change` : 'Not set'}
                          onChange={e => {
                            const v = s.numeric
                              ? e.target.value.replace(/\D/g, '').slice(0, s.maxLength || undefined)
                              : e.target.value;
                            setSensitive(prev => ({ ...prev, [s.key]: v }));
                          }}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="sale-edit-actions">
          <button className="btn btn-outline-secondary" onClick={onClose} disabled={saving}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : 'Save Changes'}
          </button>
        </div>

        <style>{`
          .sale-edit-overlay { position: fixed; inset: 0; background: rgba(0,0,0,0.5); display: flex; align-items: center; justify-content: center; z-index: 1100; padding: 1rem; }
          .sale-edit-modal { background: var(--bg-card, #fff); color: var(--text-primary, #1a1a2e); border-radius: 12px; width: 100%; max-width: 760px; max-height: 90vh; display: flex; flex-direction: column; box-shadow: 0 20px 60px rgba(0,0,0,0.3); }
          .sale-edit-header { padding: 1.25rem 1.5rem; border-bottom: 1px solid var(--border-primary, #e5e7eb); }
          .sale-edit-header h2 { margin: 0 0 0.25rem; font-size: 1.25rem; }
          .sale-edit-header small { color: var(--text-secondary, #6b7280); }
          .sale-edit-body { padding: 1rem 1.5rem; overflow-y: auto; }
          .sale-edit-section { margin-bottom: 1.25rem; }
          .sale-edit-section h3 { font-size: 0.9rem; font-weight: 600; margin: 0 0 0.6rem; color: var(--text-secondary, #6b7280); text-transform: uppercase; letter-spacing: 0.03em; }
          .sale-edit-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(220px, 1fr)); gap: 0.75rem; }
          .sale-edit-field { display: flex; flex-direction: column; }
          .sale-edit-field label { font-size: 0.8rem; margin-bottom: 0.25rem; color: var(--text-secondary, #6b7280); }
          .sale-edit-field input[type=text], .sale-edit-field input[type=number], .sale-edit-field select { padding: 0.5rem 0.6rem; border: 1px solid var(--border-primary, #e5e7eb); border-radius: 6px; background: var(--bg-primary, #fff); color: var(--text-primary, #1a1a2e); font-size: 0.9rem; }
          .sale-edit-field.is-check { flex-direction: row; align-items: center; gap: 0.5rem; }
          .sale-edit-actions { padding: 1rem 1.5rem; border-top: 1px solid var(--border-primary, #e5e7eb); display: flex; justify-content: flex-end; gap: 0.75rem; }
        `}</style>
      </div>
    </div>
  );
};
